import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Paper, Box, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Grid,
  TextField, MenuItem, Alert, Tooltip, IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import axios from '../../api/client';
import { AuthContext } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';

// Money the agency receives for its own account that is not rent, not a deposit and not a
// penalty — a letting fee, an inspection fee, bank interest.
//
// Before this screen the only correct route was an admin posting a manual journal entry,
// which carried no receipt, no payer and no category. The alternative people reached for
// was invoicing a tenant under the 'Other' charge type, which credits the LANDLORD's
// obligation and so books the agency's own fee as somebody else's money.
//
// Every receipt posts the moment it is saved. There is no draft and no approval step,
// because the money is already in hand.
const emptyForm = {
  incomeCategoryId: '',
  otherSource: '',
  amount: '',
  incomeDate: new Date().toISOString().split('T')[0],
  receivedFrom: '',
  narration: '',
  paymentModeId: '',
  receivedIntoAccountId: '',
  propertyId: ''
};

const OtherIncomeManagement = () => {
  const { can } = useContext(AuthContext);
  const { formatMoney } = useCurrency();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageTotal, setPageTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [categories, setCategories] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [properties, setProperties] = useState([]);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchRows = useCallback(async () => {
    try {
      const res = await axios.get(`/other-income?page=${page + 1}&limit=${rowsPerPage}`);
      setRows(res.data.data || []);
      setTotal(res.data.total || 0);
      setPageTotal(res.data.pageTotal || 0);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not load other income');
    }
  }, [page, rowsPerPage]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    const load = async () => {
      try {
        const [cats, modes, chart, props] = await Promise.all([
          axios.get('/other-income/categories'),
          axios.get('/payment-modes?isActive=true'),
          axios.get('/accounting/chart-of-accounts'),
          axios.get('/properties')
        ]);
        setCategories(cats.data || []);
        setPaymentModes(modes.data || []);
        const all = Array.isArray(chart.data) ? chart.data : (chart.data?.data || []);
        // Only accounts money can actually sit in. The server rejects anything else.
        setAccounts(all.filter((a) => a.type === 'asset'));
        const list = Array.isArray(props.data) ? props.data : (props.data?.data || []);
        setProperties(list);
      } catch (e) {
        // A form that cannot offer its lists is still usable for reading the table, and an
        // error toast on a screen somebody opened to look at figures is noise.
      }
    };
    load();
  }, []);

  const selectedCategory = categories.find((c) => c._id === form.incomeCategoryId) || null;
  const selectedMode = paymentModes.find((m) => m._id === form.paymentModeId) || null;
  // The account list is filtered to the kind the mode receives into, because the server
  // refuses a bank account against a cash payment and offering all of them invites that.
  const accountsForMode = selectedMode
    ? accounts.filter((a) => a.accountCategory === selectedMode.accountCategory)
    : accounts;

  const handleSave = async () => {
    if (!form.incomeCategoryId) return toast.error('Choose what this income was for');
    if (selectedCategory?.isOther && !form.otherSource.trim()) {
      return toast.error('Say where this came from');
    }
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error('Enter a valid amount');
    if (!form.narration.trim()) return toast.error('Give a narration — say what this money was for');
    if (!form.receivedIntoAccountId) return toast.error('Record which account received the money');

    setSaving(true);
    try {
      const { data } = await axios.post('/other-income', {
        ...form,
        amount: parseFloat(form.amount),
        otherSource: selectedCategory?.isOther ? form.otherSource.trim() : undefined,
        propertyId: form.propertyId || undefined,
        paymentModeId: form.paymentModeId || undefined
      });
      toast.success(`Recorded as ${data.receiptNumber}`);
      setOpen(false);
      setForm(emptyForm);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not record this income');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (row) => {
    const reason = window.prompt(
      `Cancel ${row.receiptNumber} of ${formatMoney(row.amount)}?\n\n`
      + 'This reverses its journal entry. Give a reason:'
    );
    if (reason === null) return;
    if (!reason.trim()) return toast.error('Give a reason for cancelling this receipt');
    try {
      const { data } = await axios.post(`/other-income/${row._id}/cancel`, { reason });
      toast.success(data.message);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not cancel this receipt');
    }
  };

  // What the row is FOR, in one column: the category, and for the catch-all the source
  // somebody typed, because 'Other' on its own says nothing.
  const sourceOf = (row) => (row.category?.isOther && row.otherSource
    ? `${row.category?.name}: ${row.otherSource}`
    : row.category?.name || '—');

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box>
          <Typography variant="h5">Other Income</Typography>
          <Typography variant="body2" color="textSecondary">
            Money the agency receives for its own account — not rent, not a deposit, not a penalty.
          </Typography>
        </Box>
        {can('otherIncome', 'create') && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm(emptyForm); setOpen(true); }}>
            Receive Income
          </Button>
        )}
      </Box>

      {pageTotal > 0 && (
        <Typography variant="body2" sx={{ mb: 2 }} color="textSecondary">
          {formatMoney(pageTotal)} on this page, cancelled receipts excluded.
        </Typography>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Receipt</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Narration</TableCell>
            <TableCell>From</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell>Received into</TableCell>
            <TableCell>Status</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row._id} hover>
              <TableCell>{row.receiptNumber}</TableCell>
              <TableCell>{format(new Date(row.incomeDate), 'dd MMM yy')}</TableCell>
              <TableCell>{sourceOf(row)}</TableCell>
              <TableCell sx={{ maxWidth: 280 }}>
                <Typography variant="body2" noWrap title={row.narration}>{row.narration}</Typography>
              </TableCell>
              <TableCell>{row.receivedFrom || '—'}</TableCell>
              <TableCell align="right">{formatMoney(row.amount)}</TableCell>
              <TableCell>{row.receivedIntoAccount ? `${row.receivedIntoAccount.code} ${row.receivedIntoAccount.name}` : '—'}</TableCell>
              <TableCell>
                {row.status === 'cancelled' ? (
                  <Tooltip title={row.cancellationReason || 'Cancelled'}>
                    <Chip size="small" color="default" label="Cancelled" variant="outlined" />
                  </Tooltip>
                ) : (
                  <Chip size="small" color="success" label="Received" variant="outlined" />
                )}
              </TableCell>
              <TableCell align="right">
                {row.status === 'received' && can('otherIncome', 'cancel') && (
                  <Tooltip title="Cancel and reverse its entry">
                    <IconButton size="small" onClick={() => handleCancel(row)}><BlockIcon fontSize="small" /></IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
          {!rows.length && (
            <TableRow>
              <TableCell colSpan={9}>
                <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                  Nothing recorded yet.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(e, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[25, 50, 100]}
      />

      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Receive Other Income</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                select fullWidth required label="What was this for"
                value={form.incomeCategoryId}
                onChange={(e) => setForm({ ...form, incomeCategoryId: e.target.value, otherSource: '' })}
                helperText={selectedCategory
                  ? `Posts to ${selectedCategory.accountCode}`
                  : 'Choose the source. Pick Other if nothing fits.'}
              >
                {categories.map((c) => (
                  <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* The catch-all asks for the source in words. Stored on the receipt rather than
                quietly creating a category nobody agreed to. */}
            {selectedCategory?.isOther && (
              <Grid item xs={12}>
                <TextField
                  fullWidth required label="Source"
                  value={form.otherSource}
                  onChange={(e) => setForm({ ...form, otherSource: e.target.value })}
                  helperText="Say where this money came from, in your own words"
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required type="number" label="Amount"
                value={form.amount}
                onFocus={(e) => e.target.select()}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required type="date" label="Date received"
                InputLabelProps={{ shrink: true }}
                value={form.incomeDate}
                onChange={(e) => setForm({ ...form, incomeDate: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth required multiline minRows={2} label="Narration"
                value={form.narration}
                onChange={(e) => setForm({ ...form, narration: e.target.value })}
                helperText="What this money was for. There is no invoice behind this receipt, so this is the only record of it."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth label="Received from"
                value={form.receivedFrom}
                onChange={(e) => setForm({ ...form, receivedFrom: e.target.value })}
                helperText="Optional — who paid"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select fullWidth label="How it arrived"
                value={form.paymentModeId}
                onChange={(e) => setForm({ ...form, paymentModeId: e.target.value, receivedIntoAccountId: '' })}
              >
                <MenuItem value="">Not stated</MenuItem>
                {paymentModes.map((m) => (
                  <MenuItem key={m._id} value={m._id}>{m.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select fullWidth required label="Received into"
                value={form.receivedIntoAccountId}
                onChange={(e) => setForm({ ...form, receivedIntoAccountId: e.target.value })}
                helperText={selectedMode ? `Accounts ${selectedMode.name} receives into` : 'Choose the account the money landed in'}
              >
                {accountsForMode.map((a) => (
                  <MenuItem key={a._id} value={a._id}>{a.code} {a.name}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                select fullWidth label="Property"
                value={form.propertyId}
                onChange={(e) => setForm({ ...form, propertyId: e.target.value })}
                helperText="Optional. Groups the income by building. The landlord is never credited either way."
              >
                <MenuItem value="">None</MenuItem>
                {properties.map((p) => (
                  <MenuItem key={p._id} value={p._id}>{p.propertyName}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info">
                This is the agency&rsquo;s own income. It posts as soon as you save, and never
                reaches a landlord statement or a payout.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default OtherIncomeManagement;
